import React, { useState, useEffect } from "react";
import "./DashboardAdmin.css";
import FilterChip from "../../components/Chip/FilterChip";
import DashboardCard from "../../components/Card/DashboardCard";
import UpcomingWorkshopCard from "../../components/Card/UpcomingWorkshopCard";
import CityCard from "../../components/Card/CityCard";
import AgeGroupChart from "../../components/Charts/AgeGroupChart";
import RevenueChart from "../../components/Charts/RevenueChart";
import { open_api_with_cookies } from "../../Utils/network";
import LoadingOverlay from "../../components/Loading/LoadingOverlay";

const DashboardAdmin = () => {
  const getCurrentMonthYear = () => {
    const date = new Date();
    return `${date.toLocaleString("default", {
      month: "short",
    })} ${date.getFullYear()}`;
  };

  const [timeframe, setTimeframe] = useState(getCurrentMonthYear());
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Generate dropdown options for the last 12 months + lifetime
  const generateMonthOptions = () => {
    const options = ["Lifetime"];
    const currentDate = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate);
      date.setMonth(currentDate.getMonth() - i);
      const monthYear = `${date.toLocaleString("default", {
        month: "short",
      })} ${date.getFullYear()}`;
      options.push(monthYear);
    }

    return options;
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        let url;
        if (timeframe === "Lifetime") {
          url = "/stats/dashboard/home?lifetime=true";
        } else {
          // Extract month and year from the timeframe
          const [month, year] = timeframe.split(" ");
          const monthNumber =
            new Date(Date.parse(month + " 1, " + year)).getMonth() + 1;
          url = `/stats/dashboard/home?month=${monthNumber}&year=${year}`;
        }

        const response = await open_api_with_cookies.get(url);
        setDashboardData(response.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [timeframe]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="greeting">Hello Anmol 👋</div>
        <div className="time-selector">
          <FilterChip
            options={generateMonthOptions()}
            selected={timeframe}
            onSelect={setTimeframe}
          />
        </div>
      </div>

      <div className="metrics-row">
        <DashboardCard
          title={dashboardData.metrics.website_visits.title}
          value={dashboardData.metrics.website_visits.value.toLocaleString()}
          change={dashboardData.metrics.website_visits.change_percent}
        />
        <DashboardCard
          title={dashboardData.metrics.workshop_revenue.title}
          value={dashboardData.metrics.workshop_revenue.value.toLocaleString()}
          change={dashboardData.metrics.workshop_revenue.change_percent}
          prefix="₹ "
        />
        <DashboardCard
          title={dashboardData.metrics.session_revenue.title}
          value={dashboardData.metrics.session_revenue.value.toLocaleString()}
          change={dashboardData.metrics.session_revenue.change_percent}
          prefix="₹ "
        />
        <DashboardCard
          title={dashboardData.metrics.total_revenue.title}
          value={dashboardData.metrics.total_revenue.value.toLocaleString()}
          change={dashboardData.metrics.total_revenue.change_percent}
          prefix="₹ "
        />
        <DashboardCard
          title={dashboardData.metrics.total_users.title}
          value={dashboardData.metrics.total_users.value.toLocaleString()}
          change={dashboardData.metrics.total_users.change_percent}
        />
        <DashboardCard
          title={dashboardData.metrics.app_downloads.title}
          value={dashboardData.metrics.app_downloads.value.toLocaleString()}
          change={dashboardData.metrics.app_downloads.change_percent}
        />
      </div>

      <div className="revenue-chart-container">
        <RevenueChart
          revenueData={dashboardData.revenue_graph_data.value}
          title={dashboardData.revenue_graph_data.title}
        />
      </div>

      <div className="dashboard-grid">
        <div className="left-column">
          <div className="workshop-section grid-item">
            <h3>{dashboardData.workshops_this_week.title}</h3>
            <div className="workshop-table">
              <div className="workshop-headers">
                <div className="workshop-header">Workshop</div>
                <div className="workshop-header">date/time</div>
                <div className="workshop-header">Link</div>
              </div>
              {dashboardData.workshops_this_week.value.map(
                (workshop, index) => (
                  <UpcomingWorkshopCard
                    key={index}
                    workshop={workshop.workshop__title}
                    date={formatDate(workshop.start_date_time)}
                    link={workshop.live_session_url}
                  />
                )
              )}
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="top-cities-section grid-item">
            <h3>{dashboardData.top_cities.title}</h3>
            <div className="city-table">
              <div className="city-headers">
                <div className="city-header">City</div>
                <div className="city-header">Total users</div>
              </div>
              {dashboardData.top_cities.value.map((city, index) => (
                <CityCard key={index} city={city.city} users={city.total} />
              ))}
            </div>
          </div>

          <div className="uploads-grid">
            <DashboardCard
              title={dashboardData.metrics.recipes_uploaded.title}
              value={dashboardData.metrics.recipes_uploaded.value}
              change={dashboardData.metrics.recipes_uploaded.change_percent}
            />
            <DashboardCard
              title={dashboardData.metrics.blogs_uploaded.title}
              value={dashboardData.metrics.blogs_uploaded.value}
              change={dashboardData.metrics.blogs_uploaded.change_percent}
            />
            <DashboardCard
              title={dashboardData.metrics.worksheets_uploaded.title}
              value={dashboardData.metrics.worksheets_uploaded.value}
              change={dashboardData.metrics.worksheets_uploaded.change_percent}
            />
            <DashboardCard
              title={dashboardData.metrics.worksheets_uploaded.title}
              value={dashboardData.metrics.worksheets_uploaded.value}
              change={dashboardData.metrics.worksheets_uploaded.change_percent}
            />
          </div>
        </div>

        <div className="age-groups-row">
          <div className="age-group-section grid-item">
            <AgeGroupChart
              ageGroups={dashboardData.people_age_group.value}
              title={dashboardData.people_age_group.title}
            />
          </div>

          <div className="kids-age-group-section grid-item">
            <AgeGroupChart
              ageGroups={dashboardData.kids_age_group.value}
              title={dashboardData.kids_age_group.title}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardAdmin;
